import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    RowSelectionState,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import { Cross2Icon } from "@radix-ui/react-icons";

import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { DataTableFacetedFilter } from "@/components/ui/data-table-faceted-filter";
import { Button } from "@/components/ui/button";
import { ApprovalType, ApprovalTypeLabels } from "@/models/leave-requests.model";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { LeaveType } from "@/models/leave-types.model";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AdminDataTableFacetedFilter } from "./AdminDataTableFacetedFilter";

const ApprovalTypeOptions: { label: string; value: string }[] = Object.values(
    ApprovalType
).map((type) => ({ label: ApprovalTypeLabels[type], value: type }));

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    containerClassname?: string;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    typesSelected: string[];
    setTypesSelected: Dispatch<SetStateAction<string[]>>;
    statusSelected: string[];
    setStatusSelected: Dispatch<SetStateAction<string[]>>;
}

function AdminDataTableWithToolbar({
    columns,
    data,
    containerClassname,
    search,
    setSearch,
    typesSelected,
    setTypesSelected,
    statusSelected,
    setStatusSelected,

}: DataTableProps<any, any>) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const { leaveTypes, loading, error } = useLeaveTypes();

    const [isFiltered, setIsFiltered] = useState(false);
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({})

    const [leaveTypeOptions, setLeaveTypeOptions] = useState<
        {
            label: string;
            value: string;
        }[]
    >([]);



    useEffect(() => {
        setLeaveTypeOptions(
            leaveTypes.map((leaveType: LeaveType) => ({
                label: capitalizeFirstLetter(leaveType.name),
                value: leaveType.id,
            }))
        );
    }, [leaveTypes]);


    const handleResetButton = () => {
        setSearch("");
        setTypesSelected([]);
        setStatusSelected([]);
    }

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        getFilteredRowModel: getFilteredRowModel(),

        state: {
            sorting,
            columnVisibility,
        },
    });

    useEffect(() => {
        if (typesSelected.length > 0 || statusSelected.length > 0 || search.length > 0) {
            setIsFiltered(true);
        } else {
            setIsFiltered(false);
        }
    }, [typesSelected, statusSelected, search])






    return (
        <div className={`flex flex-col h-full w-full ${containerClassname}`}>
            {/* Toolbar with sticky positioning */}
            <div className="sticky top-0 bg-white z-20 pb-2 w-full">
                <div className="flex flex-wrap items-center justify-between h-[60px] ml-1 mb-2">
                    <div className="flex flex-1 flex-wrap items-center gap-2 h-full">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                            <Input
                                placeholder="Search staff..."
                                value={
                                    search
                                }
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                }}
                                className="w-[150px] lg:w-[250px] pl-8"
                            />
                        </div>

                        <AdminDataTableFacetedFilter selectedValues={typesSelected} setSelectedValues={setTypesSelected} options={leaveTypeOptions} title="Leave Type" />
                        <AdminDataTableFacetedFilter selectedValues={statusSelected} setSelectedValues={setStatusSelected} options={ApprovalTypeOptions} title="Status" />
                        {isFiltered && (
                            <Button
                                variant="ghost"
                                onClick={handleResetButton}
                                className="h-8 px-2 lg:px-3"
                            >
                                Reset
                                <Cross2Icon className="ml-2 h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>


            {/* Table container with overflow */}
            <div className="flex-1 overflow-auto">
                <Table className="min-w-[800px]">
                    <TableHeader className="sticky top-0 bg-white z-1">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} className={`w-[${header.column.columnDef.size}px]`}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} className={` w-[${cell.column.columnDef.size}px]`}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
export default AdminDataTableWithToolbar;
